"use client";

/**
 * Games v2 on the website: a shop's games, in the storefront's white look.
 *
 *   Open now    art, name, way to win, the prize line, time left, prizes
 *               left, and Play.
 *   Coming up   name, "Live · Fri 8:00 PM · 23 waiting" / "Opens …", and
 *               Remind me (a WhatsApp before it starts).
 *   Results     name, the winners' short names, See results.
 *
 * Each group hides when it's empty; no games at all is one short line.
 * Also the small helpers the other shopper screens share (game art, sign-in,
 * readable refusals). Screens: docs/games/GAMES_V2_GUIDE.md §3.1.
 */
import Link from "next/link";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import { getFriendlyErrorMessage } from "@/lib/utils/errors";
import type { ShopList } from "./types";
import { formatCountdown, kampalaDate, kampalaDateTime, kampalaDay, kampalaTime, kampalaWhen, useNow } from "./useServerClock";

// --------------------------------------------------------- shared helpers

/** The storefront's colours (explicit, so pages look the same inside coded brand sites). */
export const SHOP = {
  ink: "#0f172a",
  body: "#334155",
  soft: "#475569",
  muted: "#64748b",
  line: "#e2e8f0",
  tint: "#f8fafc",
  art: "#f1f5f9",
} as const;

/** One game card, as listGames and getGame return it. */
type ResultCard = ShopList["results"][number];
export type GameCardData = Omit<ResultCard, "winners"> & { winners?: string[] };

/** The `code` a games refusal carries (`ConvexError({ code, message })`), if any. */
export function errorCode(e: unknown): string | null {
  const data = (e as { data?: unknown } | null | undefined)?.data;
  if (data && typeof data === "object" && typeof (data as { code?: unknown }).code === "string") {
    return (data as { code: string }).code;
  }
  return null;
}

/** The extra fields a refusal carries (`nextPlayAt`, `opensAt`…). */
export function errorData(e: unknown): Record<string, unknown> | null {
  const data = (e as { data?: unknown } | null | undefined)?.data;
  return data && typeof data === "object" ? (data as Record<string, unknown>) : null;
}

/** Did the server refuse because nobody is signed in? */
export function needsSignIn(e: unknown): boolean {
  if (errorCode(e) === "SIGN_IN_REQUIRED") return true;
  return /\bsign in\b/i.test(getFriendlyErrorMessage(e));
}

/** The sign-in page, coming back to `returnTo` (this page by default). */
export function signInHref(returnTo?: string): string {
  const back = returnTo ?? (typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "/");
  return `/signin?redirect=${encodeURIComponent(back)}`;
}

/** What shoppers call each game. */
export const GAME_NAMES: Record<string, string> = {
  spin: "Spin & Win",
  giftbox: "Gift Box Hunt",
  drop: "Prize Drop",
  trivia: "Trivia",
  match: "Product Match",
  stacker: "Style Stacker",
  grabber: "Hyper Grabber",
};

/** The way-to-win chip. */
export function wayToWin(format: string): { text: string; bg: string; fg: string } {
  if (format === "live") return { text: "Live round", bg: "#fdf2f8", fg: "#be185d" };
  if (format === "challenge") return { text: "Leaderboard", bg: "#f5f3ff", fg: "#6d28d9" };
  return { text: "Instant prizes", bg: "#ecfeff", fg: "#0e7490" };
}

/** A sentence ends with a full stop. */
export function sentence(text: string): string {
  const t = text.trim();
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

/** "29 prizes left", "1 prize left", "All prizes won", or nothing (no prizes, or prizes by place). */
export function prizesLeftText(card: { prizesLeft: number | null; prizeLine: string }): string | null {
  if (card.prizesLeft === null || card.prizeLine === "Play for fun") return null;
  if (card.prizesLeft <= 0) return "All prizes won";
  return card.prizesLeft === 1 ? "1 prize left" : `${card.prizesLeft} prizes left`;
}

/** "Opens in 25m" / "Opens today at 8:00 PM" / "Opens tomorrow at 9:00 AM" / "Opens Mon 6 Oct, 9:00 AM". */
export function opensLine(startsAt: number, now: number): string {
  const left = startsAt - now;
  if (left <= 0) return "Opening now";
  if (left < 3_600_000) return `Opens in ${formatCountdown(left)}`;
  const days = Math.round((Date.parse(kampalaDay(startsAt)) - Date.parse(kampalaDay(now))) / 86_400_000);
  if (days === 0) return `Opens today at ${kampalaTime(startsAt)}`;
  if (days === 1) return `Opens tomorrow at ${kampalaTime(startsAt)}`;
  return `Opens ${kampalaDateTime(startsAt)}`;
}

export function Chip({ text, bg, fg }: { text: string; bg: string; fg: string }) {
  return (
    <span
      className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold leading-none"
      style={{ background: bg, color: fg }}
    >
      {text}
    </span>
  );
}

// -------------------------------------------------------------------- art

/**
 * A game's picture: the owner's art, else a drawing of the game. Fills its
 * parent (give the parent a size and `position: relative`).
 */
export function GameArt({ type, artUrl, animate = false }: { type: string; artUrl?: string | null; animate?: boolean }) {
  if (artUrl) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={artUrl} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />;
  }
  return <ArtDrawing type={type} animate={animate} />;
}

const WHEEL = ["#9333ea", "#00c2d1", "#f59e0b", "#ec4899", "#22c55e", "#3b82f6", "#f97316", "#14b8a6"];

function wedge(i: number, n: number, r: number, cx: number, cy: number): string {
  const a0 = (i / n) * 2 * Math.PI;
  const a1 = ((i + 1) / n) * 2 * Math.PI;
  const p = (a: number) => `${(cx + r * Math.sin(a)).toFixed(2)} ${(cy - r * Math.cos(a)).toFixed(2)}`;
  return `M ${cx} ${cy} L ${p(a0)} A ${r} ${r} 0 0 1 ${p(a1)} Z`;
}

function ArtDrawing({ type, animate }: { type: string; animate: boolean }) {
  const spinClass = animate ? "motion-safe:animate-[spin_14s_linear_infinite]" : "";
  const common = {
    viewBox: "0 0 160 100",
    preserveAspectRatio: "xMidYMid slice",
    className: "absolute inset-0 h-full w-full",
    "aria-hidden": true,
  } as const;

  switch (type) {
    case "spin":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#f1f5f9" />
          <g className={spinClass} style={{ transformBox: "fill-box", transformOrigin: "center" }}>
            {WHEEL.map((c, i) => (
              <path key={i} d={wedge(i, 8, 40, 80, 50)} fill={c} />
            ))}
          </g>
          <circle cx="80" cy="50" r="14" fill="#ffffff" stroke="rgba(15,23,42,0.15)" strokeWidth="2" />
          <path d="M80 16 L74 5 L86 5 Z" fill="#0f172a" />
        </svg>
      );
    case "giftbox":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#faf5ff" />
          {[42, 80, 118].map((x, i) => (
            <g key={x} transform={`translate(${x - 15} ${i === 1 ? 34 : 44})`}>
              <rect x="0" y="10" width="30" height="28" rx="3" fill={i === 1 ? "#9333ea" : "#7c3aed"} />
              <rect x="-3" y="3" width="36" height="9" rx="2" fill="#a855f7" transform={i === 1 ? "rotate(-12 0 12) translate(0 -6)" : undefined} />
              <rect x="12" y="10" width="6" height="28" fill="#00c2d1" />
            </g>
          ))}
          <path d="M80 20 l3 6 6 1-5 4 1 6-5-3-5 3 1-6-5-4 6-1z" fill="#f59e0b" />
        </svg>
      );
    case "drop":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#ecfeff" />
          {[0, 1, 2, 3].map((k) =>
            Array.from({ length: k + 1 }, (_, i) => (
              <circle key={`${k}-${i}`} cx={80 + (i - k / 2) * 16} cy={22 + k * 14} r="2.6" fill="#0f172a" opacity="0.55" />
            ))
          )}
          <circle cx="72" cy="14" r="5" fill="#00c2d1" />
          {["#9333ea", "#1e293b", "#f59e0b", "#1e293b", "#22c55e"].map((c, i) => (
            <rect key={i} x={40 + i * 16 + 1} y="78" width="14" height="14" rx="3" fill={c} />
          ))}
        </svg>
      );
    case "trivia":
      return (
        <svg {...common}>
          <defs>
            <linearGradient id="games-art-trivia" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#7c3aed" />
              <stop offset="1" stopColor="#06b6d4" />
            </linearGradient>
          </defs>
          <rect width="160" height="100" fill="url(#games-art-trivia)" />
          <circle cx="58" cy="30" r="9" fill="rgba(255,255,255,0.18)" />
          <circle cx="108" cy="72" r="12" fill="rgba(255,255,255,0.14)" />
          <text x="80" y="66" textAnchor="middle" fontSize="48" fontWeight="800" fill="#ffffff" style={{ fontFamily: "system-ui, sans-serif" }}>
            ?
          </text>
        </svg>
      );
    case "match":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#fffbeb" />
          {[0, 1, 2].map((c) =>
            [0, 1].map((r) => {
              const open = (c === 0 && r === 0) || (c === 2 && r === 1);
              return (
                <g key={`${c}-${r}`}>
                  <rect x={50 + c * 21} y={24 + r * 27} width="18" height="24" rx="3" fill={open ? "#ffffff" : "#f59e0b"} stroke="#f59e0b" strokeWidth="1.5" />
                  {open ? <circle cx={59 + c * 21} cy={36 + r * 27} r="4.5" fill="#ec4899" /> : null}
                </g>
              );
            })
          )}
        </svg>
      );
    case "stacker":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#fdf2f8" />
          {[
            [58, 80, 44, "#9333ea"],
            [62, 68, 40, "#a855f7"],
            [60, 56, 38, "#ec4899"],
            [66, 44, 30, "#f472b6"],
            [64, 32, 28, "#9333ea"],
          ].map(([x, y, w, c], i) => (
            <rect key={i} x={x as number} y={y as number} width={w as number} height="11" rx="2.5" fill={c as string} />
          ))}
          <rect x="84" y="14" width="28" height="11" rx="2.5" fill="#00c2d1" opacity="0.9" />
        </svg>
      );
    case "grabber":
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#eff6ff" />
          <rect x="20" y="78" width="120" height="8" rx="4" fill="#1e293b" />
          {[34, 62, 90, 118].map((x, i) => (
            <rect key={x} x={x} y="62" width="16" height="16" rx="4" fill={["#3b82f6", "#f59e0b", "#22c55e", "#ec4899"][i]} />
          ))}
          <line x1="70" y1="0" x2="70" y2="34" stroke="#0f172a" strokeWidth="2.5" />
          <path d="M62 34 h16 M62 34 l-4 12 M78 34 l4 12" stroke="#0f172a" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <rect width="160" height="100" fill="#f1f5f9" />
        </svg>
      );
  }
}

// ------------------------------------------------------------- Remind me

export interface RemindButtonProps {
  gameId: Id<"games">;
  roundId?: Id<"gameRounds">;
  /** "chip": the small outline button on a list row; "block": the game page's big button. */
  variant?: "chip" | "block";
  /** A readable problem to show near the button (null clears it). */
  onNote?(note: string | null): void;
}

/**
 * Remind me: a WhatsApp 10 minutes before the game opens or the live round
 * starts. Tap again to turn it off. Signed-out shoppers go to sign in first.
 */
export function RemindButton({ gameId, roundId, variant = "chip", onNote }: RemindButtonProps) {
  const remindMe = useMutation(api.games.v2.shop.remindMe);
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    onNote?.(null);
    try {
      const r = await remindMe({ gameId, roundId, on: !on });
      setOn(r.on);
    } catch (e) {
      if (needsSignIn(e)) {
        window.location.href = signInHref();
        return;
      }
      onNote?.(getFriendlyErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const label = busy ? (on ? "Turning off…" : "Setting…") : on ? "Reminder on" : "Remind me";

  if (variant === "block") {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        aria-pressed={on}
        className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl px-4 text-[15px] font-bold transition active:scale-[0.99] disabled:opacity-70"
        style={on ? { background: "#ffffff", color: SHOP.ink, border: `1.5px solid ${SHOP.ink}` } : { background: SHOP.ink, color: "#ffffff" }}
      >
        {on ? <CheckIcon /> : <span className="block h-2 w-2 rounded-full" style={{ background: "#22c55e" }} aria-hidden="true" />}
        {on && !busy ? "Reminder on · we'll WhatsApp you" : label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-pressed={on}
      className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-xl px-3 text-xs font-bold transition active:scale-[0.98] disabled:opacity-70"
      style={{ border: `1px solid ${SHOP.ink}`, color: on ? "#166534" : SHOP.ink, background: on ? "#f0fdf4" : "#ffffff" }}
    >
      {on ? <CheckIcon /> : <span className="block h-2 w-2 rounded-full" style={{ background: "#22c55e" }} aria-hidden="true" />}
      {label}
    </button>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

// -------------------------------------------------------------- the list

export interface GamesListProps {
  brandId: Id<"brands">;
  /** Where a game's page lives (default "/games"). */
  basePath?: string;
}

export default function GamesList({ brandId, basePath = "/games" }: GamesListProps) {
  const data = useQuery(api.games.v2.shop.listGames, { brandId });
  const now = useNow(30_000);

  const groups = useMemo(() => {
    if (!data) return null;
    return {
      open: data.open as GameCardData[],
      upcoming: data.upcoming as GameCardData[],
      results: data.results as GameCardData[],
    };
  }, [data]);

  if (!groups) return <GamesListSkeleton />;

  if (groups.open.length === 0 && groups.upcoming.length === 0 && groups.results.length === 0) {
    return (
      <p className="py-10 text-center text-sm" style={{ color: SHOP.muted }}>
        No games right now.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6" style={{ color: SHOP.ink }}>
      {groups.open.length > 0 ? (
        <section className="flex flex-col gap-2.5">
          <GroupLabel color="#16a34a">Open now</GroupLabel>
          <div className="grid gap-3 sm:grid-cols-2">
            {groups.open.map((card) => (
              <OpenCard key={card.gameId} card={card} now={now} basePath={basePath} />
            ))}
          </div>
        </section>
      ) : null}

      {groups.upcoming.length > 0 ? (
        <section className="flex flex-col gap-2.5">
          <GroupLabel color="#db2777">Coming up</GroupLabel>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {groups.upcoming.map((card) => (
              <UpcomingRow key={card.gameId} card={card} now={now} basePath={basePath} />
            ))}
          </div>
        </section>
      ) : null}

      {groups.results.length > 0 ? (
        <section className="flex flex-col gap-2.5">
          <GroupLabel color={SHOP.muted}>Results</GroupLabel>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {groups.results.map((card) => (
              <ResultRow key={card.gameId} card={card} basePath={basePath} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function GroupLabel({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <h2 className="text-[11px] font-extrabold uppercase tracking-[0.09em]" style={{ color }}>
      {children}
    </h2>
  );
}

/** The time chip on an open game. */
function openChip(card: GameCardData, now: number | null): { text: string; bg: string; fg: string } | null {
  if (card.status === "paused") return { text: "Paused", bg: "#f1f5f9", fg: SHOP.soft };
  const round = card.format === "live" ? card.nextRound : null;
  if (round?.state === "playing") return { text: "Live now", bg: "#fee2e2", fg: "#b91c1c" };
  if (now === null) return null;
  if (round?.state === "waiting") return { text: `Live · starts ${kampalaTime(round.startsAt)}`, bg: "#fdf2f8", fg: "#be185d" };
  if (card.endsAt !== null) {
    const left = card.endsAt - now;
    if (left <= 0) return { text: "Ending now", bg: "#fef3c7", fg: "#92400e" };
    const soon = left < 86_400_000;
    return { text: `Ends in ${formatCountdown(left)}`, bg: soon ? "#fef3c7" : "#dcfce7", fg: soon ? "#92400e" : "#166534" };
  }
  return { text: "Open now", bg: "#dcfce7", fg: "#166534" };
}

function OpenCard({ card, now, basePath }: { card: GameCardData; now: number | null; basePath: string }) {
  const href = `${basePath}/${card.gameId}`;
  const chip = openChip(card, now);
  const way = wayToWin(card.format);
  const paused = card.status === "paused";
  const round = card.format === "live" ? card.nextRound : null;
  const left = prizesLeftText(card);

  let action: { text: string; href: string } | null = { text: "Play", href: `${href}?play=1` };
  if (paused) action = null;
  else if (round?.state === "waiting") action = { text: "Join", href: `${href}?play=1` };
  else if (round?.state === "playing") action = { text: "See game", href };

  let footnote = left;
  if (paused) footnote = "Paused by the shop";
  else if (round?.state === "waiting" && round.waitingCount > 0) footnote = `${round.waitingCount} waiting`;

  return (
    <article className="flex flex-col overflow-hidden rounded-2xl bg-white" style={{ border: `1px solid ${SHOP.line}` }}>
      <Link href={href} className="block">
        <div className="relative h-[120px] overflow-hidden sm:h-[140px]" style={{ background: SHOP.art }}>
          <GameArt type={card.type} artUrl={card.artUrl} animate />
        </div>
        <div className="flex flex-col gap-1.5 px-3.5 pt-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="min-w-0 text-[15px] font-bold leading-snug" style={{ color: SHOP.ink }}>
              {card.name}
            </h3>
            {chip ? <Chip {...chip} /> : null}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip {...way} />
            <span className="text-xs" style={{ color: SHOP.muted }}>
              {GAME_NAMES[card.type] ?? ""}
            </span>
          </div>
          <p className="text-[13px] leading-snug" style={{ color: SHOP.soft }}>
            {sentence(card.prizeLine)}
          </p>
        </div>
      </Link>
      <div className="mt-auto flex items-center justify-between gap-3 px-3.5 pb-3.5 pt-2.5">
        <span className="min-w-0 text-xs" style={{ color: SHOP.muted }}>
          {footnote ?? ""}
        </span>
        {action ? (
          <Link
            href={action.href}
            className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-xl px-5 text-sm font-bold transition active:scale-[0.98]"
            style={{ background: SHOP.ink, color: "#ffffff" }}
          >
            {action.text}
          </Link>
        ) : null}
      </div>
    </article>
  );
}

function upcomingMeta(card: GameCardData, now: number | null): string {
  if (now === null) return "";
  const round = card.format === "live" ? card.nextRound : null;
  if (round) {
    const waiting = round.waitingCount > 0 ? ` · ${round.waitingCount} waiting` : "";
    return `Live · ${kampalaWhen(round.startsAt, now)}${waiting}`;
  }
  if (card.startsAt !== null) return opensLine(card.startsAt, now);
  return "Coming soon";
}

function UpcomingRow({ card, now, basePath }: { card: GameCardData; now: number | null; basePath: string }) {
  const [note, setNote] = useState<string | null>(null);
  const href = `${basePath}/${card.gameId}`;
  const round = card.format === "live" ? card.nextRound : null;
  return (
    <div className="flex flex-col gap-1.5 rounded-xl bg-white p-2.5" style={{ border: `1px solid ${SHOP.line}` }}>
      <div className="flex items-center gap-3">
        <Link href={href} className="flex min-w-0 flex-1 items-center gap-3">
          <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-lg" style={{ background: SHOP.art }}>
            <GameArt type={card.type} artUrl={card.artUrl} />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-bold" style={{ color: SHOP.ink }}>
              {card.name}
            </p>
            <p className="truncate text-xs" style={{ color: SHOP.muted }}>
              {upcomingMeta(card, now)}
            </p>
          </div>
        </Link>
        <RemindButton gameId={card.gameId} roundId={round?.roundId} onNote={setNote} />
      </div>
      {note ? (
        <p className="px-1 text-xs" style={{ color: "#b91c1c" }} role="status">
          {note}
        </p>
      ) : null}
    </div>
  );
}

function ResultRow({ card, basePath }: { card: GameCardData; basePath: string }) {
  const href = `${basePath}/${card.gameId}#results`;
  const winners = card.winners ?? [];
  const meta = winners.length > 0 ? `Won by ${winners.join(", ")}` : card.endsAt !== null ? `Ended ${kampalaDate(card.endsAt)}` : "Ended";
  return (
    <div className="flex items-center gap-3 rounded-xl bg-white p-2.5" style={{ border: `1px solid ${SHOP.line}` }}>
      <Link href={href} className="flex min-w-0 flex-1 items-center gap-3">
        <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-lg" style={{ background: SHOP.art }}>
          <GameArt type={card.type} artUrl={card.artUrl} />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-bold" style={{ color: SHOP.ink }}>
            {card.name}
          </p>
          <p className="truncate text-xs" style={{ color: SHOP.muted }}>
            {meta}
          </p>
        </div>
      </Link>
      <Link
        href={href}
        className="inline-flex min-h-11 shrink-0 items-center rounded-xl px-3 text-xs font-bold"
        style={{ border: `1px solid ${SHOP.ink}`, color: SHOP.ink, background: "#ffffff" }}
      >
        See results
      </Link>
    </div>
  );
}

/** Grey blocks in the list's shape while it loads. */
export function GamesListSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-label="Loading games">
      <div className="h-3 w-20 animate-pulse rounded" style={{ background: SHOP.line }} />
      <div className="grid gap-3 sm:grid-cols-2">
        {[0, 1].map((i) => (
          <div key={i} className={`overflow-hidden rounded-2xl bg-white ${i === 1 ? "hidden sm:block" : ""}`} style={{ border: `1px solid ${SHOP.line}` }}>
            <div className="h-[120px] animate-pulse sm:h-[140px]" style={{ background: SHOP.art }} />
            <div className="flex flex-col gap-2 p-3.5">
              <div className="h-4 w-2/3 animate-pulse rounded" style={{ background: SHOP.line }} />
              <div className="h-3 w-1/3 animate-pulse rounded" style={{ background: SHOP.line }} />
              <div className="h-3 w-full animate-pulse rounded" style={{ background: SHOP.line }} />
              <div className="mt-1 flex items-center justify-between">
                <div className="h-3 w-24 animate-pulse rounded" style={{ background: SHOP.line }} />
                <div className="h-11 w-20 animate-pulse rounded-xl" style={{ background: SHOP.line }} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2 h-3 w-24 animate-pulse rounded" style={{ background: SHOP.line }} />
      <div className="h-[66px] animate-pulse rounded-xl" style={{ background: SHOP.art }} />
    </div>
  );
}
